import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageCircle, BookOpen, Heart, PenTool } from "lucide-react";
import { motion } from "framer-motion";

const cards = [
  {
    icon: MessageCircle,
    title: "Just Chat",
    desc: "Tell us what you're in the mood for — a genre, a feeling, a theme. No browsing, no algorithms. Just conversation.",
  },
  {
    icon: BookOpen,
    title: "Get Recommendations",
    desc: "Our AI draws from a curated collection of indie books, complete with author quotes and rich descriptions, to find your perfect match.",
  },
  {
    icon: Heart,
    title: "Discover & Support",
    desc: "Fall in love with stories you'd never have found otherwise, and support indie authors directly.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: "easeOut" as const },
  }),
};

export default function About() {
  return (
    <article className="bookstore-grain flex-1 overflow-y-auto" aria-label="About Shhh... Book Club">
      <div className="mx-auto max-w-3xl px-4 py-16 md:py-24">
        {/* Hero */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
            Why We Whisper
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Where indie stories find their readers
          </p>
        </motion.div>

        {/* The Problem */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <h2 className="mb-5 font-display text-2xl font-bold text-foreground">
            The Problem
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground">
            The world is full of incredible stories written by indie authors who pour their hearts into their craft — yet most of them never reach the readers who'd love them. The big publishers are loud. We think the best books deserve something different: a whisper that cuts through the noise.
          </p>
        </motion.section>

        {/* How We Do It */}
        <section className="mb-16">
          <motion.h2
            className="mb-8 font-display text-2xl font-bold text-foreground"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.5 }}
          >
            How We Do It
          </motion.h2>
          <div className="grid gap-6 md:grid-cols-3">
            {cards.map((item, i) => (
              <motion.div
                key={item.title}
                className="rounded-2xl border border-border/50 bg-card p-6 shadow-sm"
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                custom={i + 3}
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <item.icon size={20} />
                </div>
                <h3 className="mb-2 font-display text-lg font-bold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <motion.section
          className="rounded-2xl bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 p-8 text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
        >
          <PenTool className="mx-auto mb-4 text-primary" size={32} />
          <h2 className="mb-2 font-display text-2xl font-bold text-foreground">
            Are You an Indie Author?
          </h2>
          <p className="mb-6 text-muted-foreground">
            We'd love to feature your work. Submit your book and let readers discover your story.
          </p>
          <Button asChild size="lg" className="rounded-xl font-semibold">
            <Link to="/submit">Submit Your Book</Link>
          </Button>
        </motion.section>
      </div>
    </article>
  );
}
